import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { inputClass, labelClass } from "@/lib/constants";

export default function AdminCategories() {
  const [categories, setCategories] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", sort_order: 0 });

  const load = () => api.get("/admin/categories").then((r) => setCategories(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/categories", form);
      toast.success("Category created");
      setForm({ name: "", description: "", sort_order: 0 });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (c) => {
    await api.put(`/admin/categories/${c.id}`, { name: c.name, description: c.description || "", sort_order: c.sort_order, active: !c.active }).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Products in it must be reassigned first.`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <h1 className="font-serif text-2xl font-bold text-cream-text">Categories</h1>

      <form onSubmit={create} className="mt-6 flex flex-wrap items-end gap-3 rounded-3xl border border-choco-line bg-choco-light p-5" data-testid="new-category-form">
        <div>
          <label htmlFor="cat-name" className={labelClass}>Name</label>
          <input id="cat-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} data-testid="category-name-input" />
        </div>
        <div className="min-w-60 flex-1">
          <label htmlFor="cat-desc" className={labelClass}>Description</label>
          <input id="cat-desc" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={inputClass} data-testid="category-desc-input" />
        </div>
        <div>
          <label htmlFor="cat-order" className={labelClass}>Sort order</label>
          <input id="cat-order" type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({ ...f, sort_order: parseInt(e.target.value || "0", 10) }))} className={`${inputClass} w-24`} data-testid="category-order-input" />
        </div>
        <button type="submit" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-choco hover:bg-gold-hover" data-testid="create-category-button">
          <Plus className="h-4 w-4" aria-hidden="true" /> Add
        </button>
      </form>

      <div className="mt-6 space-y-3" data-testid="categories-list">
        {(categories || []).map((c) => (
          <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-choco-line bg-choco-light p-4" data-testid={`category-row-${c.slug}`}>
            <div>
              <p className="font-semibold text-cream-text">{c.name} <span className="ml-2 text-xs text-cream-muted">/{c.slug} · {c.product_count} product(s)</span></p>
              {c.description && <p className="mt-0.5 text-xs text-cream-muted">{c.description}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggleActive(c)} className={`rounded-full px-4 py-1.5 text-xs font-bold ${c.active ? "bg-gold/20 text-gold" : "border border-choco-line text-cream-muted"}`} data-testid={`category-active-${c.slug}`}>
                {c.active ? "Active" : "Hidden"}
              </button>
              <button onClick={() => remove(c)} aria-label={`Delete ${c.name}`} className="rounded-full p-2 text-cream-muted hover:text-red-400" data-testid={`category-delete-${c.slug}`}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
